import React from 'react';
import { X, Mail, RefreshCw } from 'lucide-react';
import { replaceMergeFields, getCategoryLabel, getCategoryColor, type EmailTemplate } from '../../../services/queries/emailTemplateQueries';
import { StandardButton } from '../../standard';

interface TemplatePreviewModalProps {
  template: EmailTemplate | null;
  isOpen: boolean;
  onClose: () => void;
}

export const TemplatePreviewModal: React.FC<TemplatePreviewModalProps> = ({
  template,
  isOpen,
  onClose
}) => {
  if (!isOpen || !template) return null;

  const previewSubject = replaceMergeFields(template.subject);
  const previewBody = replaceMergeFields(template.body);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-gray-900 dark:text-white">Template Preview</h2>
            <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">
              Preview with sample data
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="space-y-6">
            {/* Template Info */}
            <div className="bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
                  <Mail className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="font-semibold text-purple-900 dark:text-purple-300">
                      {template.name}
                    </h3>
                    <span className={`px-2 py-0.5 text-xs font-medium rounded ${getCategoryColor(template.category)}`}>
                      {getCategoryLabel(template.category)}
                    </span>
                    {template.isSystem && (
                      <span className="px-2 py-0.5 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 text-xs rounded font-medium">
                        System
                      </span>
                    )}
                  </div>
                  {template.description && (
                    <p className="text-sm text-purple-800 dark:text-purple-300">
                      {template.description}
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Sample Data Info */}
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
              <div className="flex items-start gap-2">
                <RefreshCw className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-blue-800 dark:text-blue-300">
                  <p className="font-semibold mb-1">Preview Mode:</p>
                  <p>Merge fields have been replaced with sample data. The actual email will use real data when sent.</p>
                </div>
              </div>
            </div>

            {/* Email Preview */}
            <div className="bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-lg overflow-hidden shadow-lg">
              {/* Email Header */}
              <div className="bg-gray-100 dark:bg-gray-800 border-b border-gray-300 dark:border-gray-600 p-4">
                <div className="space-y-2">
                  <div className="flex items-start gap-3">
                    <span className="text-sm font-medium text-gray-600 dark:text-gray-400 w-16">From:</span>
                    <span className="text-sm text-gray-900 dark:text-white">Product Hub &lt;noreply@producthub.com&gt;</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <span className="text-sm font-medium text-gray-600 dark:text-gray-400 w-16">To:</span>
                    <span className="text-sm text-gray-900 dark:text-white">john.doe@example.com</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <span className="text-sm font-medium text-gray-600 dark:text-gray-400 w-16">Subject:</span>
                    <span className="text-sm font-semibold text-gray-900 dark:text-white">{previewSubject}</span>
                  </div>
                </div>
              </div>

              {/* Email Body */}
              <div className="p-6 bg-white">
                {/* Always use light background for email preview since emails are typically light */}
                <div
                  className="prose prose-sm max-w-none prose-headings:text-gray-900 prose-p:text-gray-700 prose-a:text-purple-600 prose-strong:text-gray-900"
                  dangerouslySetInnerHTML={{ __html: previewBody }}
                />
              </div>
            </div>

            {/* Merge Fields Used */}
            {template.mergeFields.length > 0 && (
              <div>
                <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">
                  Merge Fields Used ({template.mergeFields.length})
                </h4>
                <div className="flex flex-wrap gap-2">
                  {template.mergeFields.map(field => (
                    <span
                      key={field}
                      className="px-3 py-1 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 text-xs font-mono rounded-full"
                    >
                      {`{{${field}}}`}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Template Stats */}
            <div className="grid grid-cols-3 gap-4">
              <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-3 border border-gray-200 dark:border-gray-700">
                <div className="text-xs text-gray-600 dark:text-gray-400 mb-1">Times Sent</div>
                <div className="text-lg font-bold text-gray-900 dark:text-white">
                  {template.usageCount.toLocaleString()}
                </div>
              </div>
              <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-3 border border-gray-200 dark:border-gray-700">
                <div className="text-xs text-gray-600 dark:text-gray-400 mb-1">Version</div>
                <div className="text-lg font-bold text-gray-900 dark:text-white">
                  {template.version}
                </div>
              </div>
              <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-3 border border-gray-200 dark:border-gray-700">
                <div className="text-xs text-gray-600 dark:text-gray-400 mb-1">Last Used</div>
                <div className="text-lg font-bold text-gray-900 dark:text-white">
                  {template.lastUsed
                    ? new Date(template.lastUsed).toLocaleDateString()
                    : 'Never'
                  }
                </div>
              </div>
            </div>

            {/* Sample Data Reference */}
            <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
              <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">
                Sample Data Used in Preview
              </h4>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <div className="text-gray-600 dark:text-gray-400 mb-2 font-medium">User Fields</div>
                  <div className="space-y-1 text-xs">
                    <div><span className="text-gray-500 dark:text-gray-400">First Name:</span> <span className="text-gray-900 dark:text-white">John</span></div>
                    <div><span className="text-gray-500 dark:text-gray-400">Last Name:</span> <span className="text-gray-900 dark:text-white">Doe</span></div>
                    <div><span className="text-gray-500 dark:text-gray-400">Email:</span> <span className="text-gray-900 dark:text-white">john.doe@example.com</span></div>
                    <div><span className="text-gray-500 dark:text-gray-400">Phone:</span> <span className="text-gray-900 dark:text-white">+1 (555) 123-4567</span></div>
                  </div>
                </div>
                <div>
                  <div className="text-gray-600 dark:text-gray-400 mb-2 font-medium">Client Fields</div>
                  <div className="space-y-1 text-xs">
                    <div><span className="text-gray-500 dark:text-gray-400">Name:</span> <span className="text-gray-900 dark:text-white">Acme Corporation</span></div>
                    <div><span className="text-gray-500 dark:text-gray-400">Email:</span> <span className="text-gray-900 dark:text-white">contact@acme.com</span></div>
                    <div><span className="text-gray-500 dark:text-gray-400">Phone:</span> <span className="text-gray-900 dark:text-white">+1 (555) 987-6543</span></div>
                  </div>
                </div>
                <div>
                  <div className="text-gray-600 dark:text-gray-400 mb-2 font-medium">Case Fields</div>
                  <div className="space-y-1 text-xs">
                    <div><span className="text-gray-500 dark:text-gray-400">Number:</span> <span className="text-gray-900 dark:text-white">CASE-00123</span></div>
                    <div><span className="text-gray-500 dark:text-gray-400">Subject:</span> <span className="text-gray-900 dark:text-white">Technical Support Request</span></div>
                    <div><span className="text-gray-500 dark:text-gray-400">Status:</span> <span className="text-gray-900 dark:text-white">In Progress</span></div>
                  </div>
                </div>
                <div>
                  <div className="text-gray-600 dark:text-gray-400 mb-2 font-medium">System Fields</div>
                  <div className="space-y-1 text-xs">
                    <div><span className="text-gray-500 dark:text-gray-400">Date:</span> <span className="text-gray-900 dark:text-white">{new Date().toLocaleDateString()}</span></div>
                    <div><span className="text-gray-500 dark:text-gray-400">Time:</span> <span className="text-gray-900 dark:text-white">{new Date().toLocaleTimeString()}</span></div>
                    <div><span className="text-gray-500 dark:text-gray-400">Company:</span> <span className="text-gray-900 dark:text-white">Product Hub</span></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex items-center justify-end gap-3 bg-gray-50 dark:bg-gray-900">
          <StandardButton
            variant="ghost"
            onClick={onClose}
          >
            Close
          </StandardButton>
        </div>
      </div>
    </div>
  );
};
